import { Divider } from "antd";
import React, { useEffect, useRef, useState } from 'react';
import { DELIVERY_TYPE } from "../../../../../util/varibles/constants";
import { addClassName, cloneObj, getElById, removeClassName } from "../../../../../util/varibles/global";
import { IIdName, IOperation, IUnit, TOperation } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { getKeyUnit } from "../../../util";
import { getEditOp, setEditOp, useEditOp } from "../EditOperationContext";
import { DRAG_MODE } from "./constants";
import styles from "./style.module.scss";

interface IProps {
    data: TOperation
}

const DragOperation: React.FC<IProps> = ({ data: { id, operation_code } }) => {
    const set = setEditOp();
    const get = getEditOp();
    const [dragData] = useEditOp(state => state.dragData);
    const isDragging = id === dragData?.id;
    const isDrag = !!dragData?.id
    const [{ left, top }, setPosition] = useState({ left: 0, top: 0 });
    const rootRef = useRef<HTMLDivElement>(null);
    const positionRef = useRef<{ relX: number, relY: number }>({ relX: 0, relY: 0 });

    const handleMouseMove = useRef((e: MouseEvent) => {
        const { pageX, pageY, target } = e;
        const { relX, relY } = positionRef.current;
        if (rootRef.current) {
            if ((target as HTMLDivElement)?.dataset.opId) {
                rootRef.current.style.display = 'none'
            } else {
                rootRef.current.style.display = ''
            }
        }

        setPosition({ left: pageX - relX, top: pageY - relY });
    })

    const handleMouseUp = useRef((e: MouseEvent) => {
        const el = getElById(id);
        if (el) {
            el.dataset.dragging = 'false';
        }

        removeClassName(document.body, 'no-select cursor-grabbing');

        if (rootRef.current)
            rootRef.current.style.display = '';

        const { target } = e;
        const { opId = '', job } = (target as HTMLDivElement)?.dataset || {};
        const state: { dragData: undefined, harvests?: IOperation[] } = { dragData: undefined };

        if (opId.length > 0) {
            try {
                const harvests: ({ isCall: boolean } & IOperation)[] = cloneObj(get().harvests) || [];
                const targetIndex = harvests.findIndex(item => item.operation.id === opId);
                const currentIndex = harvests.findIndex(item => item.operation.id === id);
                if (job === DRAG_MODE.SWAP) {
                    const temp = harvests[targetIndex];
                    harvests[targetIndex] = harvests[currentIndex];
                    harvests[currentIndex] = temp;
                } else {
                    let {
                        sub_operations: targetSubs = [],
                        total_weight: targetTotalWeight,
                        factory_id: targetFactoryId
                    } = harvests[targetIndex].operation || {};
                    const {
                        sub_operations: currentSubs = [],
                        total_weight: currentTotalWeight,
                        factory_id: currentFactoryId
                    } = harvests[currentIndex].operation || {};
                    const { capacity } = harvests[targetIndex].vessel;
                    if ((targetTotalWeight + currentTotalWeight) > capacity) {
                        notify.error(NotifyCode.E1)();
                        set(state);
                        return;
                    }
                    let { fish_amount, total_amount } = harvests[targetIndex].operation;
                    let sub_operations;
                    if (targetFactoryId === currentFactoryId) {
                        sub_operations = currentSubs.reduce((rs: IUnit[], item) => {
                            const key = getKeyUnit(item);
                            const index = rs.findIndex(sub => getKeyUnit(sub) === key);
                            const {
                                fish_amount: subFishAmount,
                                total_weight: subTotalWeight,
                                delivery_types: subDeliveryTypes = []
                            } = item;
                            targetTotalWeight += subTotalWeight;
                            fish_amount += subFishAmount;
                            total_amount = fish_amount;

                            if (index > -1) {
                                rs[index] = {
                                    ...rs[index],
                                    fish_amount: rs[index].fish_amount + subFishAmount,
                                    total_weight: subTotalWeight,
                                    delivery_types: subDeliveryTypes.reduce((subRs: typeof subDeliveryTypes, deliveryType) => {
                                        const indexDeliveryType = subRs.findIndex(type => type.id === deliveryType.id);
                                        if (indexDeliveryType > -1) {
                                            subRs[indexDeliveryType].total_weight += deliveryType.total_weight;
                                            subRs[indexDeliveryType].fish_amount += deliveryType.fish_amount;
                                            subRs[indexDeliveryType].tanks = [];
                                        } else {
                                            subRs = [...subRs, { ...deliveryType, tanks: [] }]
                                        }
                                        return subRs
                                    }, rs[index].delivery_types || [])
                                }
                                return rs;
                            } else {
                                return [...rs, {
                                    ...item,
                                    delivery_types: (item.delivery_types || []).map(deliveryType => ({
                                        ...deliveryType,
                                        tanks: []
                                    }))
                                }]
                            }
                        }, targetSubs)
                    } else {
                        const directSubs = currentSubs.reduce((rs: IUnit[], item) => {
                            const value = (item.delivery_types || []).find(sub => sub.type === DELIVERY_TYPE.DIRECT);
                            return value ? [...rs, {
                                ...item,
                                fish_amount: value.fish_amount,
                                total_weight: value.total_weight,
                                delivery_types: [{ ...value, tanks: [] }]
                            }] : [...rs, { ...item, fish_amount: 0, total_weight: 0, delivery_types: [] }]
                        }, []);
                        sub_operations = directSubs.reduce((rs: IUnit[], item) => {
                            const key = getKeyUnit(item);
                            const index = rs.findIndex(sub => getKeyUnit(sub) === key);
                            const {
                                fish_amount: subFishAmount,
                                total_weight: subTotalWeight,
                                delivery_types: subDeliveryTypes = []
                            } = item;
                            targetTotalWeight += subTotalWeight;
                            fish_amount += subFishAmount;
                            total_amount = fish_amount;

                            if (index > -1) {
                                if (subDeliveryTypes.length === 0)
                                    return rs;
                                rs[index] = {
                                    ...rs[index],
                                    fish_amount: rs[index].fish_amount + subFishAmount,
                                    total_weight: subTotalWeight,
                                    delivery_types: subDeliveryTypes.reduce((subRs: typeof subDeliveryTypes, deliveryType) => {
                                        const indexDeliveryType = subRs.findIndex(type => type.type === deliveryType.type);
                                        if (indexDeliveryType > -1) {
                                            subRs[indexDeliveryType].total_weight += deliveryType.total_weight;
                                            subRs[indexDeliveryType].fish_amount += deliveryType.fish_amount;
                                            subRs[indexDeliveryType].tanks = [];
                                        } else {
                                            subRs = [...subRs, { ...deliveryType, tanks: [] }]
                                        }
                                        return subRs
                                    }, rs[index].delivery_types || [])
                                }
                                return rs;
                            } else {
                                return [...rs, item]
                            }
                        }, targetSubs)
                    }
                    const groupBySite = sub_operations.reduce((rs: { [id: string]: string }, item) => {
                        rs[item.site_id || ''] = item.site_name || ''
                        return rs
                    }, {})
                    harvests[targetIndex] = {
                        ...harvests[targetIndex],
                        isCall: true,
                        operation: {
                            ...harvests[targetIndex].operation,
                            total_weight: targetTotalWeight,
                            fish_amount,
                            total_amount,
                            sub_operations,
                            sites: Object.keys(groupBySite).reduce((rs: IIdName[], key) => [...rs, {
                                id: key,
                                name: groupBySite[key]
                            }], [])
                        }
                    }
                    harvests.splice(currentIndex, 1);
                }
                state.harvests = harvests
            } catch (e) {
                console.log(e)
            }
        }
        set(state);
        document.removeEventListener('mousemove', handleMouseMove.current);
        document.removeEventListener('mouseup', handleMouseUp.current);
    })

    useEffect(() => {
        return () => {
            set({ dragData: undefined })
            removeClassName(document.body, 'no-select cursor-grabbing')

            // eslint-disable-next-line react-hooks/exhaustive-deps
            document.removeEventListener('mousemove', handleMouseMove.current);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            document.removeEventListener('mouseup', handleMouseUp.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0)
            return;
        const { pageX, pageY } = e;
        if (rootRef.current) {
            const { x = 0, y = 0 } = rootRef.current?.getBoundingClientRect() || {};
            const el = getElById(id);
            if (el) {
                el.dataset.dragging = 'true';
            }
            positionRef.current = { relX: pageX - x, relY: pageY - y };
            rootRef.current.style.position = '';
            addClassName(document.body, 'no-select cursor-grabbing')
            setPosition({ left: pageX - (pageX - x), top: pageY - (pageY - y) });

            document.addEventListener('mousemove', handleMouseMove.current);
            document.addEventListener('mouseup', handleMouseUp.current);
            set({ dragData: { id } })
        }
    }

    const handleMouseOver = ({ target }: React.MouseEvent) => {
        if (target)
            (target as HTMLDivElement).dataset.hover = 'true';
    }
    const handleMouseLeave = ({ target }: React.MouseEvent) => {
        if (target)
            (target as HTMLDivElement).dataset.hover = '';
    }

    return <>
        {((!isDrag && !isDragging) || isDragging) && <div
            ref={rootRef}
            className={styles.drag}
            onMouseDown={handleMouseDown}
            data-dragging={isDragging}
            style={{ top: `${top}px`, left: `${left}px` }}
        >
            <div className="text-[15px] font-[600]">
                Trip #{operation_code}
            </div>
        </div>}
        {(isDrag && !isDragging) && <div className={styles.dragOpts}>
            <div
                className={styles.dragOpt}
                data-op-id={id}
                data-job={DRAG_MODE.SWAP}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
            >
                Swap position of Trip #{operation_code}
            </div>
            <Divider orientation='vertical' className='h-[22px] mt-[1px]' />
            <div
                className={styles.dragOpt}
                data-op-id={id}
                data-job={DRAG_MODE.MERGE}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
            >
                Merge into Trip #{operation_code}
            </div>
        </div>}
    </>;
};

export default DragOperation;